import { useMemo, useState, type FormEvent } from 'react';
import { Alert, Box, Button, MenuItem, Stack, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { patchMe } from '@/api/settings';
import { countryName, countryOptions } from '@/i18n/countries';
import { useLocale } from '@/i18n/useLocale';
import { useMe, useMeRefetch } from '@/session/meHooks';
import { defaultTimezone, isValidTimezone } from '@/features/subscribe/timezone';
import type { SettingsNotify } from './settingsHref';
import { useUnsavedGuard } from '@/shared/useUnsavedGuard';
import { UnsavedChangesDialog } from '@/components/UnsavedChangesDialog';
import { useErrorFocus } from '@/shared/useErrorFocus';
import { sameValues } from '@/shared/useDirty';

const DISPLAY_NAME_MAX = 120;

interface ProfileTabProps {
  onNotify: SettingsNotify;
}

export function ProfileTab({ onNotify }: ProfileTabProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const refetchMe = useMeRefetch();
  const [displayName, setDisplayName] = useState(me.user.displayName);
  const [timezone, setTimezone] = useState(me.user.timezone ?? defaultTimezone());
  // The saved country is the member's own; a tenant default is help text, not a saved value
  // (TMMAPP-0165 item 18). A served country with no source predates TMMAPI-0196 U5: it's the member's.
  const tenantCountry = me.user.countrySource === 'TENANT' ? me.user.country : null;
  const servedCountry = tenantCountry === null ? (me.user.country ?? '') : '';
  const [country, setCountry] = useState(servedCountry);
  const countries = useMemo(() => countryOptions(locale), [locale]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  const trimmedName = displayName.trim();
  const trimmedZone = timezone.trim();
  const timezoneInvalid = !isValidTimezone(timezone);
  const nameChanged = trimmedName !== me.user.displayName && trimmedName.length > 0;
  const zoneChanged = trimmedZone !== (me.user.timezone ?? defaultTimezone());
  const countryChanged = country !== servedCountry;
  const canSave = (nameChanged || zoneChanged || countryChanged) && !timezoneInvalid && !busy;
  // Dirty = the fields differ from what is saved (`me` follows a save, so a save makes it clean).
  // A `?section=` link is a query-only move, held like any navigation (includeSearch).
  const dirty =
    !sameValues(displayName, me.user.displayName) ||
    !sameValues(timezone, me.user.timezone ?? defaultTimezone()) ||
    countryChanged;
  const guard = useUnsavedGuard(dirty, { busy, includeSearch: true });
  const { rootRef, focusErrors } = useErrorFocus<HTMLFormElement>();

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSave) return;
    setBusy(true);
    setError(false);
    const body: { displayName?: string; timezone?: string; country?: string } = {};
    if (nameChanged) body.displayName = trimmedName;
    if (zoneChanged) body.timezone = trimmedZone;
    // An empty string clears it (TMMAPI-0195 U2).
    if (countryChanged) body.country = country;
    try {
      await patchMe(body);
      await refetchMe();
      onNotify(t('settings.saved'));
    } catch {
      setError(true);
      focusErrors();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box component="form" ref={rootRef} noValidate onSubmit={handleSubmit} sx={{ maxWidth: 560 }}>
      <UnsavedChangesDialog guard={guard} />
      <Stack spacing={2.5}>
        {error && <Alert severity="error">{t('settings.errorGeneric')}</Alert>}
        <TextField
          label={t('settings.displayName')}
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
          fullWidth
          slotProps={{ htmlInput: { maxLength: DISPLAY_NAME_MAX } }}
        />
        <TextField
          label={t('subscribe.timezoneLabel')}
          value={timezone}
          onChange={(event) => setTimezone(event.target.value)}
          required
          fullWidth
          error={timezoneInvalid}
          helperText={timezoneInvalid ? t('subscribe.timezoneInvalid') : undefined}
        />
        <TextField
          select
          label={t('settings.country')}
          value={country}
          onChange={(event) => setCountry(event.target.value)}
          fullWidth
          helperText={
            tenantCountry !== null && country === ''
              ? t('settings.countryDefault', { country: countryName(tenantCountry, locale) })
              : t('settings.countryHelp')
          }
          slotProps={{
            select: {
              displayEmpty: true,
              MenuProps: { slotProps: { paper: { sx: { maxHeight: 360 } } } },
            },
            inputLabel: { shrink: true },
          }}
        >
          <MenuItem value="">{t('settings.countryNone')}</MenuItem>
          {countries.map((option) => (
            <MenuItem key={option.code} value={option.code}>
              {option.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label={t('settings.email')}
          value={me.user.email}
          fullWidth
          disabled
          helperText={t('settings.emailManaged')}
        />
        <Box>
          <Button type="submit" variant="contained" disabled={!canSave}>
            {t('common.save')}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
